import { formatCurrency } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SHIPPING_MARKUP_POLICY_PCT } from "$convex/helpers/shippingQuote";
import { roundToCents } from "../bill-shared";

export interface OrderFreight {
  orderId: string;
  orderNumber: string;
  quoteId?: string;
  quotedAmount?: number;
  quoteEstimated?: boolean;
  markupAmount?: number;
  markupPercent?: number;
  chargedAmount?: number;
}

export function OrderFreightComparison({
  freight,
  invoiceAmount,
  currencyConfirmed,
}: {
  freight: OrderFreight;
  invoiceAmount: number | undefined;
  currencyConfirmed: boolean;
}) {
  // Cherry may deploy before Convex. The older response used quotedAmount for
  // the customer charge, so never present it as the carrier quote.
  if (freight.quoteEstimated === undefined) {
    return (
      <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-xs">
        <p className="font-medium">{freight.orderNumber} freight comparison</p>
        <p className="text-muted-foreground">
          Quote details are unavailable. Check the internal invoice before reviewing this carrier bill.
        </p>
      </div>
    );
  }

  const variance =
    currencyConfirmed &&
    freight.quotedAmount !== undefined &&
    invoiceAmount !== undefined
      ? roundToCents(invoiceAmount - freight.quotedAmount)
      : undefined;
  const matches = variance === 0;

  return (
    <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-xs">
      <p className="font-medium">{freight.orderNumber} freight comparison</p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5">
        {freight.quoteId && (
          <>
            <dt className="text-muted-foreground">Shipping Quote ID</dt>
            <dd className="break-all text-right font-medium">{freight.quoteId}</dd>
          </>
        )}
        <dt className="text-muted-foreground">Shipping quote amount</dt>
        <dd className="text-right font-medium tabular-nums">
          {freight.quotedAmount !== undefined
            ? formatCurrency(freight.quotedAmount)
            : "Not available"}
        </dd>
        <dt className="text-muted-foreground">Shipping charged to customer</dt>
        <dd className="text-right font-medium tabular-nums">
          {freight.chargedAmount !== undefined
            ? formatCurrency(freight.chargedAmount)
            : "Not available"}
        </dd>
        {freight.markupAmount !== undefined && (
          <>
            <dt className="text-muted-foreground">
              Markup{freight.markupPercent !== undefined ? ` (${freight.markupPercent}%)` : ""}
            </dt>
            <dd className="text-right font-medium tabular-nums">
              {formatCurrency(freight.markupAmount)}
            </dd>
          </>
        )}
        <dt className="text-muted-foreground">Invoice total</dt>
        <dd className="text-right font-medium tabular-nums">
          {invoiceAmount !== undefined && currencyConfirmed
            ? formatCurrency(invoiceAmount)
            : "Not available in USD"}
        </dd>
      </dl>
      {freight.quoteEstimated && (
        <p className="text-muted-foreground">
          Quote estimated from the {SHIPPING_MARKUP_POLICY_PCT}% markup policy; no carrier rate was saved.
        </p>
      )}
      {variance !== undefined ? (
        <p
          className={cn(
            "font-medium tabular-nums",
            matches
              ? "text-emerald-700 dark:text-emerald-400"
              : "text-amber-700 dark:text-amber-400",
          )}
        >
          {matches
            ? "Invoice matches quote"
            : `${formatCurrency(Math.abs(variance))} ${variance > 0 ? "over" : "under"} quote`}
        </p>
      ) : (
        <p className="text-muted-foreground">
          {!currencyConfirmed
            ? "Confirm the invoice is in USD to compare amounts."
            : invoiceAmount === undefined
              ? "Enter the invoice total to compare amounts."
              : "No shipping quote is available for comparison."}
        </p>
      )}
      <p className="text-muted-foreground">
        Compares the full invoice with this order’s freight. The customer charge
        does not confirm payment was collected.
      </p>
    </div>
  );
}
